import React, { useCallback, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { useT } from "@/lib/LanguageContext";
// Relative, not "@/...": no "@" alias on the Jest side — same reasoning as the sibling
// PracticeNameCard.
import useFieldErrors from "../../hooks/useFieldErrors";
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import useAdminLoad, { payloadShapeError } from "../../hooks/useAdminLoad";
import LoadFailure from "../../components/ui/LoadFailure";

const PATH = "/tenant/professional-membership";

// The SAME bounds the server enforces in `professional_membership.py`
// (MAX_ASSOCIATION_NAME, MAX_MEMBERSHIP_NUMBER). Both fields are optional: "" means "not
// stated", and a consultation receipt then simply prints no row for it. The client half
// answers instantly; the server is the authority, and its 422 lands in the catch below.
const ASSOCIATION_MAX = 120;
const NUMBER_MAX = 40;
const MEMBERSHIP_SCHEMA = z.object({
  association_name: z.string().max(ASSOCIATION_MAX, "settings.professionalMembership.associationTooLong"),
  membership_number: z.string().max(NUMBER_MAX, "settings.professionalMembership.numberTooLong"),
});

const asText = (v) => (typeof v === "string" ? v : "");

/**
 * The practitioner's professional association and membership number.
 *
 * Insurers reimburse a consultation only when its receipt names the practitioner's
 * association and membership number, so every CONSULTATION receipt (paid appointments,
 * in person or by video, and per-minute consultations) issued after a save prints them,
 * with the date of service. Receipts for products, courses and other offerings never do,
 * and receipts already issued keep what they were issued with. The card owns its read
 * and write: the values live on the practice's own `tenants` document, behind
 * `/tenant/professional-membership`, not in SettingsAdmin's key/value form.
 */
export default function ProfessionalMembershipCard() {
  const t = useT("admin");
  const tc = useT("common");
  const { fieldErrors, validate, clearFieldError } = useFieldErrors();
  const [values, setValues] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(PATH);
    if (!data || typeof data !== "object" || Array.isArray(data)) throw payloadShapeError();
    setValues({
      association_name: asText(data.association_name),
      membership_number: asText(data.membership_number),
    });
  }, []);
  const { loadError, reload } = useAdminLoad(load);

  const update = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    clearFieldError(key);
    setSaved(false);
  };

  const save = async () => {
    const body = {
      association_name: (values.association_name || "").trim(),
      membership_number: (values.membership_number || "").trim(),
    };
    if (validate(MEMBERSHIP_SCHEMA, body)) {
      toast.error(tc("validation.fixHighlighted"));
      return;
    }
    setBusy(true);
    setSaved(false);
    try {
      const { data } = await api.put(PATH, body);
      setValues({
        association_name: asText(data?.association_name ?? body.association_name),
        membership_number: asText(data?.membership_number ?? body.membership_number),
      });
      setSaved(true);
    } catch (e) {
      toast.error(extractApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, testId, label, maxLength) => (
    <div>
      <label className="text-xs uppercase tracking-[0.18em] text-eh-muted block mb-1">{label}</label>
      <input
        type="text"
        maxLength={maxLength}
        data-testid={testId}
        value={values[key]}
        onChange={update(key)}
        className={`w-full bg-white border rounded-xl px-3 py-2 text-sm text-eh-ink outline-none focus:border-eh-sage-deep ${fieldErrors[key] ? invalidInputClass : "border-eh-border-soft"}`}
      />
      <FieldError message={fieldErrors[key] ? t(fieldErrors[key]) : ""} testId={`${testId}-error`} />
    </div>
  );

  return (
    <div
      data-testid="professional-membership-card"
      className="mt-8 bg-eh-cream border border-eh-border-soft rounded-3xl p-7 space-y-5 max-w-3xl"
    >
      <div>
        <h2 className="font-display text-2xl text-eh-ink">{t("settings.professionalMembership.title")}</h2>
        <p className="text-eh-muted text-sm mt-1">{t("settings.professionalMembership.subtitle")}</p>
      </div>

      {loadError ? (
        <LoadFailure
          message={extractApiError(loadError, tc("errors.loadFailed"), tc)}
          onRetry={reload}
          retryLabel={tc("actions.retry")}
          testId="professional-membership-load-failure"
          retryTestId="professional-membership-retry"
        />
      ) : values === null ? (
        <Loader2 className="animate-spin text-eh-muted" size={18} />
      ) : (
        <>
          {field("association_name", "professional-membership-association",
            t("settings.professionalMembership.associationLabel"), ASSOCIATION_MAX)}
          {field("membership_number", "professional-membership-number",
            t("settings.professionalMembership.numberLabel"), NUMBER_MAX)}
          <p className="text-xs text-eh-muted" data-testid="professional-membership-hint">
            {t("settings.professionalMembership.hint")}
          </p>

          <div className="flex items-center gap-3">
            <button
              data-testid="professional-membership-save"
              onClick={save}
              disabled={busy}
              className="btn-primary text-sm"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}{" "}
              {t("settings.professionalMembership.save")}
            </button>
            {saved && (
              <span className="text-sm text-eh-sage-deep" data-testid="professional-membership-saved">
                ✓ {t("settings.professionalMembership.saved")}
              </span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
